import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Lock,
  Copy,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Key,
  AlertTriangle,
} from "lucide-react";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

// Reserved/disallowed environment variable names (must align with backend)
const DISALLOWED_ENV_VAR_NAMES = new Set([
  "_HANDLER",
  "_X_AMZN_TRACE_ID",
  "AWS_DEFAULT_REGION",
  "AWS_REGION",
  "AWS_EXECUTION_ENV",
  "AWS_LAMBDA_FUNCTION_NAME",
  "AWS_LAMBDA_FUNCTION_MEMORY_SIZE",
  "AWS_LAMBDA_FUNCTION_VERSION",
  "AWS_LAMBDA_INITIALIZATION_TYPE",
  "AWS_LAMBDA_LOG_GROUP_NAME",
  "AWS_LAMBDA_LOG_STREAM_NAME",
  "AWS_ACCESS_KEY",
  "AWS_ACCESS_KEY_ID",
  "AWS_SECRET_ACCESS_KEY",
  "AWS_SESSION_TOKEN",
  "AWS_LAMBDA_RUNTIME_API",
  "LAMBDA_TASK_ROOT",
  "LAMBDA_RUNTIME_DIR",
]);

interface EnvironmentVariable {
  id: string;
  name: string;
  value?: string;
  encrypted_value?: string;
  is_encrypted: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface EnvironmentVariablesSectionProps {
  workspace: any;
  workspaceId: string;
  api: any;
}

interface DeleteConfirmationState {
  isOpen: boolean;
  envVarId: string | null;
  envVarName: string | null;
}

const EnvironmentVariablesSection: React.FC<
  EnvironmentVariablesSectionProps
> = ({ workspace, workspaceId, api }) => {
  const { isReadOnly } = useReadOnlyMode();
  const [envVars, setEnvVars] = useState<EnvironmentVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form states for adding new variable
  const [newVarName, setNewVarName] = useState("");
  const [newVarValue, setNewVarValue] = useState("");
  const [newVarDescription, setNewVarDescription] = useState("");
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [addingVariable, setAddingVariable] = useState(false);

  // Form states for editing
  const [editForm, setEditForm] = useState<{
    id: string;
    name: string;
    value: string;
    description: string;
    is_encrypted: boolean;
  } | null>(null);

  // States for showing/hiding values
  const [visibleValues, setVisibleValues] = useState<Set<string>>(new Set());
  const [loadingValues, setLoadingValues] = useState<Set<string>>(new Set());

  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmationState>({
      isOpen: false,
      envVarId: null,
      envVarName: null,
    });

  const isExpoTemplate = workspace?.template_slug === "expo-template";
  
  // Only restrict reserved names for vibe mode (deployable) projects
  const isVibeMode = workspace?.project_mode === "vibe";

  // Load environment variables
  useEffect(() => {
    loadEnvironmentVariables();
  }, [workspaceId]);

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadEnvironmentVariables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/workspaces/${workspaceId}/environment-variables`,
      );

      // API returns data directly in response
      const envVarsArray = Array.isArray(response) ? response : [];
      setEnvVars(envVarsArray);
    } catch (err: any) {
      console.error("Failed to load environment variables:", err);
      setError("Failed to load environment variables");
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariable = async () => {
    if (!newVarName || !newVarValue) {
      setToast({ message: "Name and value are required", type: "error" });
      return;
    }

    // Validate name format
    if (!/^[A-Z][A-Z0-9_]*$/.test(newVarName)) {
      setToast({
        message:
          "Variable names must be uppercase letters, numbers, and underscores, starting with a letter",
        type: "error",
      });
      return;
    }

    // Reserved names are indicated inline; allow submission so backend can handle validation if needed

    try {
      setAddingVariable(true);
      await api.post(`/workspaces/${workspaceId}/environment-variables`, {
        name: newVarName,
        value: newVarValue,
        description: newVarDescription,
        is_encrypted: isEncrypted,
      });

      // Reset form
      setNewVarName("");
      setNewVarValue("");
      setNewVarDescription("");
      setIsEncrypted(false);
      setShowAddForm(false);

      // Reload variables
      await loadEnvironmentVariables();
      setToast({
        message: "Environment variable added successfully",
        type: "success",
      });
    } catch (err: any) {
      console.error("Failed to add environment variable:", err);
      setToast({
        message:
          err.detail || err.message || "Failed to add environment variable",
        type: "error",
      });
    } finally {
      setAddingVariable(false);
    }
  };

  const startEdit = (envVar: EnvironmentVariable) => {
    setEditingId(envVar.id);
    setEditForm({
      id: envVar.id,
      name: envVar.name,
      value: envVar.value || "",
      description: envVar.description || "",
      is_encrypted: envVar.is_encrypted,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
    if (!editForm) return;

    try {
      // Validate name format if provided
      if (editForm.name && !/^[A-Z][A-Z0-9_]*$/.test(editForm.name)) {
        setToast({
          message:
            "Variable names must be uppercase letters, numbers, and underscores, starting with a letter",
          type: "error",
        });
        return;
      }

      // Disallow reserved names on rename only for vibe mode (deployable) projects
      if (isVibeMode && editForm.name && DISALLOWED_ENV_VAR_NAMES.has(editForm.name)) {
        setToast({
          message: `\"${editForm.name}\" is reserved by the platform and cannot be set`,
          type: "error",
        });
        return;
      }

      setSavingId(editForm.id);
      await api.put(
        `/workspaces/${workspaceId}/environment-variables/${editForm.id}`,
        {
          name: editForm.name,
          value: editForm.value,
          description: editForm.description,
          is_encrypted: editForm.is_encrypted,
        },
      );

      setEditingId(null);
      setEditForm(null);
      await loadEnvironmentVariables();
      setToast({
        message: "Environment variable updated successfully",
        type: "success",
      });
    } catch (err: any) {
      console.error("Failed to update environment variable:", err);
      setToast({
        message:
          err.detail || err.message || "Failed to update environment variable",
        type: "error",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteVariable = (envVarId: string, envVarName: string) => {
    setDeleteConfirmation({
      isOpen: true,
      envVarId,
      envVarName,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.envVarId) return;

    try {
      setDeletingId(deleteConfirmation.envVarId);
      await api.delete(
        `/workspaces/${workspaceId}/environment-variables/${deleteConfirmation.envVarId}`,
      );
      await loadEnvironmentVariables();
      setToast({
        message: "Environment variable deleted successfully",
        type: "success",
      });
    } catch (err: any) {
      console.error("Failed to delete environment variable:", err);
      setToast({
        message:
          err.detail || err.message || "Failed to delete environment variable",
        type: "error",
      });
    } finally {
      setDeletingId(null);
      setDeleteConfirmation({
        isOpen: false,
        envVarId: null,
        envVarName: null,
      });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, envVarId: null, envVarName: null });
  };

  const toggleValueVisibility = async (envVarId: string) => {
    const newVisibleValues = new Set(visibleValues);

    if (visibleValues.has(envVarId)) {
      newVisibleValues.delete(envVarId);
      setVisibleValues(newVisibleValues);
    } else {
      // For encrypted values, we might need to fetch the decrypted value
      const envVar = envVars.find((v) => v.id === envVarId);
      if (envVar?.is_encrypted && !envVar.value) {
        try {
          setLoadingValues(new Set(loadingValues).add(envVarId));
          const response = await api.get(
            `/workspaces/${workspaceId}/environment-variables/${envVarId}/value`,
          );

          // Update the local state with decrypted value
          // Backend returns {value: "decrypted_value"}
          const decryptedValue = response.value || response;
          setEnvVars((prevVars) =>
            prevVars.map((v) =>
              v.id === envVarId ? { ...v, value: decryptedValue } : v,
            ),
          );
        } catch (err) {
          console.error("Failed to decrypt value:", err);
          setToast({ message: "Failed to decrypt value", type: "error" });
          return;
        } finally {
          setLoadingValues((prev) => {
            const newSet = new Set(prev);
            newSet.delete(envVarId);
            return newSet;
          });
        }
      }

      newVisibleValues.add(envVarId);
      setVisibleValues(newVisibleValues);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setToast({ message: `${label} copied to clipboard`, type: "success" });
      })
      .catch(() => {
        setToast({ message: "Failed to copy to clipboard", type: "error" });
      });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          Environment Variables
        </h2>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Manage environment variables for your project. Sensitive values can be
          encrypted for security.
        </p>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p className="text-sm">
            Environment variable changes take effect only after a sandbox reset.
            After adding or editing variables, reset your project's sandbox.
            <br />
            <br />
            {isExpoTemplate && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Note: Only environment variables prefixed with `EXPO_PUBLIC_` are recognized in Expo mobile apps.
              </span>
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Add new variable button */}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          disabled={isReadOnly}
          className={`mb-6 px-4 py-2 rounded-xl flex items-center gap-2 transition-colors ${
            isReadOnly
              ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
              : 'bg-brand-action hover:bg-brand-action-hover text-white dark:text-black'
          }`}
          title={isReadOnly ? 'Upgrade to add environment variables' : 'Add a new environment variable'}
        >
          <Plus size={16} />
          Add Variable
        </button>
      )}

      {/* Add new variable form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
            Add New Variable
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value.toUpperCase())}
                placeholder="DATABASE_URL"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action"
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Uppercase letters, numbers, and underscores only
              </p>
              {isVibeMode && DISALLOWED_ENV_VAR_NAMES.has(newVarName) && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  This variable name is reserved by the platform and cannot be
                  set.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newVarValue}
                onChange={(e) => setNewVarValue(e.target.value)}
                placeholder="Enter value"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newVarDescription}
                onChange={(e) => setNewVarDescription(e.target.value)}
                placeholder="Optional description"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="encrypt-new"
                checked={isEncrypted}
                onChange={(e) => setIsEncrypted(e.target.checked)}
                className="w-4 h-4 text-brand-action border-neutral-300 rounded focus:ring-brand-action"
              />
              <label
                htmlFor="encrypt-new"
                className="ml-2 text-sm text-neutral-700 dark:text-neutral-300"
              >
                Encrypt this value
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleAddVariable}
                disabled={
                  addingVariable || (isVibeMode && DISALLOWED_ENV_VAR_NAMES.has(newVarName))
                }
                className="px-4 py-2 bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {addingVariable ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Add Variable
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewVarName("");
                  setNewVarValue("");
                  setNewVarDescription("");
                  setIsEncrypted(false);
                }}
                className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg flex items-center gap-2 transition-colors"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Environment variables list */}
      <div className="space-y-3">
        {envVars.length === 0 ? (
          <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
            <Key size={48} className="mx-auto mb-4 opacity-20" />
            <p>No environment variables yet</p>
            <p className="text-sm mt-2">
              Add your first environment variable to get started
            </p>
          </div>
        ) : (
          envVars.map((envVar) => (
            <div
              key={envVar.id}
              className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-4 bg-white dark:bg-neutral-800/50 hover:shadow-sm transition-shadow"
            >
              {editingId === envVar.id && editForm ? (
                // Edit mode
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            name: e.target.value.toUpperCase(),
                          })
                        }
                        className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                        Value
                      </label>
                      <input
                        type="text"
                        value={editForm.value}
                        onChange={(e) =>
                          setEditForm({ ...editForm, value: e.target.value })
                        }
                        className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`encrypt-${envVar.id}`}
                        checked={editForm.is_encrypted}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            is_encrypted: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-brand-action border-neutral-300 rounded focus:ring-brand-action"
                      />
                      <label
                        htmlFor={`encrypt-${envVar.id}`}
                        className="ml-2 text-sm text-neutral-700 dark:text-neutral-300"
                      >
                        Encrypt this value
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={savingId === envVar.id}
                        className="px-3 py-1 text-sm bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1 transition-colors"
                      >
                        {savingId === envVar.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Check size={14} />
                            Save
                          </>
                        )}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1 text-sm border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg flex items-center gap-1 transition-colors"
                      >
                        <X size={14} />
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Display mode
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-mono font-medium text-neutral-900 dark:text-white">
                        {envVar.name}
                      </h3>
                      {envVar.is_encrypted && (
                        <span title="Encrypted">
                          <Lock
                            size={16}
                            className="text-amber-600 dark:text-amber-500"
                          />
                        </span>
                      )}
                      <button
                        onClick={() =>
                          copyToClipboard(envVar.name, "Variable name")
                        }
                        className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                        title="Copy name"
                      >
                        <Copy
                          size={14}
                          className="text-neutral-500 dark:text-neutral-400"
                        />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(envVar)}
                        disabled={isReadOnly}
                        className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to edit' : 'Edit'}
                      >
                        <Edit2
                          size={16}
                          className="text-neutral-500 dark:text-neutral-400"
                        />
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteVariable(envVar.id, envVar.name)
                        }
                        disabled={deletingId === envVar.id || isReadOnly}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to delete' : 'Delete'}
                      >
                        {deletingId === envVar.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                        ) : (
                          <Trash2
                            size={16}
                            className="text-red-500 dark:text-red-400"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">
                      Value:
                    </span>
                    <code className="flex-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded text-sm font-mono text-neutral-700 dark:text-neutral-300 overflow-hidden">
                      {visibleValues.has(envVar.id) && envVar.value
                        ? envVar.value
                        : envVar.is_encrypted
                          ? "••••••••••••••••"
                          : envVar.value || ""}
                    </code>
                    {envVar.is_encrypted && (
                      <button
                        onClick={() => toggleValueVisibility(envVar.id)}
                        disabled={loadingValues.has(envVar.id)}
                        className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50"
                        title={
                          visibleValues.has(envVar.id)
                            ? "Hide value"
                            : "Show value"
                        }
                      >
                        {loadingValues.has(envVar.id) ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-500"></div>
                        ) : visibleValues.has(envVar.id) ? (
                          <EyeOff
                            size={16}
                            className="text-neutral-500 dark:text-neutral-400"
                          />
                        ) : (
                          <Eye
                            size={16}
                            className="text-neutral-500 dark:text-neutral-400"
                          />
                        )}
                      </button>
                    )}
                    {((!envVar.is_encrypted && envVar.value) ||
                      (visibleValues.has(envVar.id) && envVar.value)) && (
                      <button
                        onClick={() =>
                          copyToClipboard(envVar.value || "", "Value")
                        }
                        className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                        title="Copy value"
                      >
                        <Copy
                          size={14}
                          className="text-neutral-500 dark:text-neutral-400"
                        />
                      </button>
                    )}
                  </div>

                  {envVar.description && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                      {envVar.description}
                    </p>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                Delete Environment Variable
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Are you sure you want to delete the environment variable{" "}
                <span className="font-mono font-medium text-neutral-900 dark:text-white">
                  "{deleteConfirmation.envVarName}"
                </span>
                ? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId === deleteConfirmation.envVarId}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {deletingId === deleteConfirmation.envVarId ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentVariablesSection;
